import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { recordAudit } from '@/services/audit';
import { canWrite, WRITE_PERMISSION_DENIED } from '@/services/permissions';

// Forma canónica de talla que consumen las páginas (coincide con el mock).
export interface SizeItem {
  id: string;
  name: string;
  label: string;
  sizeRange: string;
  sortOrder: number;
  active: boolean;
  // Peso aproximado (kg) usado para el cálculo de flete de Interrapidísimo.
  weightKg: number;
  // Tipo de talla: 'premium' o 'basica' (define qué productos la pueden usar).
  sizeType: string;
}

// Forma canónica de inventario que consumen las páginas (coincide con el mock).
export interface InventoryItem {
  id: string;
  variantId: string;
  productId: string;
  sizeId: string;
  quantityAvailable: number;
  quantityReserved: number;
  quantityConsigned: number;
  unitCost: number;
  location: string;
  minStock: number;
}

// Movimiento de inventario (snapshot inmutable de la regla de oro).
export interface InventoryMovement {
  id: string;
  variantId: string;
  productName: string;
  sizeName: string;
  sku: string;
  movementType: string;
  quantity: number;
  prevAvailable: number;
  newAvailable: number;
  unitCost: number;
  reason: string | null;
  createdAt: string;
}

export interface InventoryMovementsResult {
  movements: InventoryMovement[];
}

export interface InventoryResult {
  inventory: InventoryItem[];
  sizes: SizeItem[];
}

// Filas reales de Supabase.
interface DbSize {
  id: string;
  name: string;
  description: string | null;
  cm_range: string | null;
  sort_order: number | null;
  is_active: boolean | null;
  weight_kg: number | string | null;
  size_type: string | null;
}

interface DbVariant {
  id: string;
  product_id: string;
  size_id: string;
  sku: string;
  min_stock_alert: number | null;
  is_active: boolean | null;
}

interface DbInventory {
  id: string;
  variant_id: string;
  available: number;
  reserved: number;
  consigned: number;
}

interface DbProductCost {
  id: string;
  unit_cost: number | string;
}

function mapSize(row: DbSize): SizeItem {
  return {
    id: row.id,
    name: row.name,
    label: row.description ?? '',
    sizeRange: row.cm_range ?? '',
    sortOrder: row.sort_order ?? 0,
    active: row.is_active ?? true,
    weightKg: Number(row.weight_kg ?? 0.5),
    sizeType: row.size_type ?? 'premium',
  };
}

// Consulta el inventario real (variantes + tallas + costos) con fallback a mocks.
// El esquema físico no tiene `location`, así que se deja vacío; el costo unitario
// se obtiene del producto y el stock mínimo de la variante (min_stock_alert).
export async function fetchInventory(): Promise<InventoryResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { inventory: [], sizes: [] };
  }

  try {
    const [productsRes, sizesRes, variantsRes, inventoryRes] = await Promise.all([
      supabase.from('products').select('id, unit_cost'),
      supabase.from('sizes').select('*'),
      supabase.from('product_variants').select('*'),
      supabase.from('inventory').select('*'),
    ]);

    if (productsRes.error) throw new Error(productsRes.error.message);
    if (sizesRes.error) throw new Error(sizesRes.error.message);
    if (variantsRes.error) throw new Error(variantsRes.error.message);
    if (inventoryRes.error) throw new Error(inventoryRes.error.message);

    const variants = (variantsRes.data ?? []) as DbVariant[];
    const inventoryRows = (inventoryRes.data ?? []) as DbInventory[];
    const sizeRows = (sizesRes.data ?? []) as DbSize[];
    const productRows = (productsRes.data ?? []) as DbProductCost[];

    const unitCostMap = new Map(productRows.map((p) => [p.id, Number(p.unit_cost)]));
    const variantMap = new Map(variants.map((v) => [v.id, v]));

    const inventory = inventoryRows.map((row) => {
      const variant = variantMap.get(row.variant_id);
      const productId = variant?.product_id ?? '';
      return {
        id: row.id,
        variantId: row.variant_id,
        productId,
        sizeId: variant?.size_id ?? '',
        quantityAvailable: row.available,
        quantityReserved: row.reserved,
        quantityConsigned: row.consigned,
        unitCost: unitCostMap.get(productId) ?? 0,
        location: '',
        minStock: variant?.min_stock_alert ?? 0,
      };
    });

    return {
      inventory,
      sizes: sizeRows.map(mapSize),
    };
  } catch (err) {
    console.error('[inventory] Supabase connection failed:', err);
    throw err;
  }
}

interface DbMovement {
  id: string;
  variant_id: string;
  movement_type: string;
  quantity: number;
  prev_available: number;
  new_available: number;
  unit_cost: number | string;
  reason: string | null;
  created_at: string;
}

// Registra cambios de inventario en audit_logs (no bloqueante). Recibe los
// campos modificados con su valor anterior y nuevo para trazabilidad completa.
export interface InventoryChange {
  fieldName: string;
  oldValue: number;
  newValue: number;
}

export async function logInventoryChange(
  variantId: string,
  changes: InventoryChange[],
): Promise<void> {
  for (const c of changes) {
    await recordAudit({
      entityType: 'inventory',
      entityId: variantId,
      action: 'UPDATE',
      fieldName: c.fieldName,
      oldValue: String(c.oldValue),
      newValue: String(c.newValue),
    });
  }
}

// Consulta el historial de movimientos de inventario (snapshots inmutables)
// desnormalizando variante -> producto + talla + SKU. Sin datos, devuelve vacío.
export async function fetchInventoryMovements(): Promise<InventoryMovementsResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { movements: [] };
  }

  try {
    const [movementsRes, variantsRes, productsRes, sizesRes] = await Promise.all([
      supabase.from('inventory_movements').select('*'),
      supabase.from('product_variants').select('*'),
      supabase.from('products').select('id, name'),
      supabase.from('sizes').select('*'),
    ]);

    if (movementsRes.error) throw new Error(movementsRes.error.message);
    if (variantsRes.error) throw new Error(variantsRes.error.message);
    if (productsRes.error) throw new Error(productsRes.error.message);
    if (sizesRes.error) throw new Error(sizesRes.error.message);

    const movements = (movementsRes.data ?? []) as DbMovement[];
    if (movements.length === 0) {
      return { movements: [] };
    }

    const variantMap = new Map(
      ((variantsRes.data ?? []) as Array<Record<string, any>>).map((v) => [v.id, v]),
    );
    const productMap = new Map(
      ((productsRes.data ?? []) as Array<Record<string, any>>).map((p) => [p.id, p.name]),
    );
    const sizeMap = new Map(
      ((sizesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.name]),
    );

    const data = movements
      .map((m) => {
        const variant = variantMap.get(m.variant_id);
        const productName = variant ? productMap.get(variant.product_id) ?? '' : '';
        const sizeName = variant ? sizeMap.get(variant.size_id) ?? '' : '';
        return {
          id: m.id,
          variantId: m.variant_id,
          productName,
          sizeName,
          sku: variant?.sku ?? '',
          movementType: m.movement_type,
          quantity: m.quantity,
          prevAvailable: m.prev_available,
          newAvailable: m.new_available,
          unitCost: Number(m.unit_cost),
          reason: m.reason,
          createdAt: m.created_at,
        };
      })
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

    return { movements: data };
  } catch (err) {
    console.error('[inventory] movements connection failed:', err);
    throw err;
  }
}

// Consulta (sin mutar) cuántos pedidos de checkout en línea abandonado serían
// liberados por el barrido, para pedir confirmación antes de ejecutar.
export async function previewExpiredReservations(
  minutes = 45,
): Promise<{ count: number; error: string | null }> {
  if (!isSupabaseConfigured || !supabase) {
    return { count: 0, error: 'Supabase no está configurado.' };
  }

  try {
    const { data, error } = await supabase.functions.invoke(
      'release-expired-reservations',
      { body: { minutes, preview: true } },
    );
    if (error) return { count: 0, error: error.message };
    const count = Number((data as { count?: number } | null)?.count ?? 0);
    return { count, error: null };
  } catch (err) {
    console.warn('[inventory] preview reservations failed:', err);
    return { count: 0, error: 'Error de conexión al consultar reservas.' };
  }
}

// Fuerza manualmente el barrido de reservas vencidas (pedidos de checkout
// abandonados) invocando la Edge Function, sin depender del cron externo.
// Devuelve cuántos pedidos se liberaron.
export async function releaseExpiredReservations(
  minutes = 45,
): Promise<{ released: number; error: string | null }> {
  if (!(await canWrite('inventory'))) {
    return { released: 0, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { released: 0, error: 'Supabase no está configurado.' };
  }

  try {
    const { data, error } = await supabase.functions.invoke(
      'release-expired-reservations',
      { body: { minutes } },
    );
    if (error) return { released: 0, error: error.message };
    const payload = data as { released?: number; status?: string; message?: string | null } | null;
    if (payload?.status === 'error') {
      return { released: 0, error: payload.message || 'El barrido falló al ejecutarse.' };
    }
    const released = Number(payload?.released ?? 0);
    return { released, error: null };
  } catch (err) {
    console.warn('[inventory] release reservations failed:', err);
    return { released: 0, error: 'Error de conexión al liberar reservas.' };
  }
}

// Reserva activa de stock: agrupa, por pedido pendiente, las unidades retenidas
// (bucket `reserved` del inventario) para poder auditar el desglose en el panel.
export interface ActiveReservation {
  orderId: string;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  status: string;
  paymentMethod: string;
  createdAt: string;
  reservedUnits: number;
}

export interface ActiveReservationsResult {
  reservations: ActiveReservation[];
  totalUnits: number;
}

// Deriva las reservas activas de los pedidos no-producción que aún retienen
// stock (pendientes de pago). Es una vista informativa: si falla, devuelve vacío
// para no romper la carga principal del inventario.
export async function fetchActiveReservations(): Promise<ActiveReservationsResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { reservations: [], totalUnits: 0 };
  }

  try {
    const [ordersRes, itemsRes, customersRes] = await Promise.all([
      supabase
        .from('orders')
        .select('id, order_number, status, created_at, payment_method, production, customer_id, recipient_name, recipient_phone'),
      supabase.from('order_items').select('order_id, quantity'),
      supabase.from('customers').select('id, first_name, last_name, phone'),
    ]);

    if (ordersRes.error) throw new Error(ordersRes.error.message);
    if (itemsRes.error) throw new Error(itemsRes.error.message);
    if (customersRes.error) throw new Error(customersRes.error.message);

    const reservingStatuses = new Set(['pending_payment', 'payment_initiated']);
    const customerMap = new Map(
      ((customersRes.data ?? []) as Array<Record<string, any>>).map((c) => [c.id, c]),
    );

    const unitsByOrder = new Map<string, number>();
    for (const it of (itemsRes.data ?? []) as Array<Record<string, any>>) {
      unitsByOrder.set(it.order_id, (unitsByOrder.get(it.order_id) ?? 0) + Number(it.quantity ?? 0));
    }

    const reservations = ((ordersRes.data ?? []) as Array<Record<string, any>>)
      .filter((o) => !o.production && reservingStatuses.has(o.status))
      .map((o) => {
        const c = customerMap.get(o.customer_id);
        const name = c
          ? `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()
          : String(o.recipient_name ?? '');
        const phone = String(c?.phone ?? o.recipient_phone ?? '').trim();
        return {
          orderId: o.id,
          orderNumber: o.order_number,
          customerName: name,
          customerPhone: phone,
          status: o.status,
          paymentMethod: o.payment_method ?? '',
          createdAt: o.created_at,
          reservedUnits: unitsByOrder.get(o.id) ?? 0,
        };
      })
      .filter((r) => r.reservedUnits > 0)
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

    const totalUnits = reservations.reduce((s, r) => s + r.reservedUnits, 0);
    return { reservations, totalUnits };
  } catch (err) {
    console.warn('[inventory] active reservations failed:', err);
    return { reservations: [], totalUnits: 0 };
  }
}

// Ajusta el stock disponible de una variante respetando la "regla de oro":
// nunca se sobrescribe el número a lo bruto. Se actualiza el `available` y se
// inserta un movimiento inmutable (ADJUSTMENT) con el antes/después completo
// (disponible, reservado y consignado) para que todo quede trazado.
export async function adjustInventoryStock(
  item: InventoryItem,
  newAvailable: number,
  reason: string,
): Promise<{ error: string | null }> {
  if (!(await canWrite('inventory'))) {
    return { error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  const quantity = newAvailable - item.quantityAvailable;

  try {
    const { error: updateErr } = await supabase
      .from('inventory')
      .update({ available: newAvailable })
      .eq('variant_id', item.variantId);

    if (updateErr) return { error: updateErr.message };

    let userId: string | null = null;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      userId = sessionData.session?.user?.id ?? null;
    } catch {
      userId = null;
    }

    const { error: moveErr } = await supabase.from('inventory_movements').insert({
      variant_id: item.variantId,
      movement_type: 'ADJUSTMENT',
      quantity,
      prev_available: item.quantityAvailable,
      new_available: newAvailable,
      prev_reserved: item.quantityReserved,
      new_reserved: item.quantityReserved,
      prev_consigned: item.quantityConsigned,
      new_consigned: item.quantityConsigned,
      unit_cost: item.unitCost,
      reason: reason.trim() || null,
      user_id: userId,
    });

    if (moveErr) return { error: moveErr.message };

    recordAudit({
      entityType: 'inventory',
      entityId: item.variantId,
      action: 'UPDATE',
      fieldName: 'available',
      oldValue: String(item.quantityAvailable),
      newValue: String(newAvailable),
    });

    return { error: null };
  } catch (err) {
    console.warn('[inventory] adjust failed:', err);
    return { error: 'Error de conexión al ajustar el stock.' };
  }
}

// Estado del barrido automático de reservas vencidas (cron). Se lee desde una
// función segura que consulta cron.job_run_details para dar confianza de que el
// proceso está vivo. Si falla, devuelve null sin romper la carga del inventario.
export interface ReservationSweepStatus {
  active: boolean;
  intervalMinutes: number;
  lastRun: {
    status: string | null;
    source: string | null;
    releasedCount: number | null;
    startTime: string | null;
    endTime: string | null;
    message: string | null;
  } | null;
}

// Una corrida del barrido (automática o manual) registrada en el histórico.
export interface ReservationSweepRun {
  id: string;
  source: string;
  status: string;
  releasedCount: number;
  minutes: number;
  message: string | null;
  startedAt: string;
  finishedAt: string;
  createdAt: string;
}

// Historial de las últimas corridas del barrido (éxito/error + pedidos liberados).
// Si falla, devuelve una lista vacía sin romper la vista de inventario.
export async function fetchReservationSweepHistory(
  limit = 12,
): Promise<ReservationSweepRun[]> {
  if (!isSupabaseConfigured || !supabase) return [];
  try {
    const { data, error } = await supabase.rpc('get_reservation_sweep_history', {
      p_limit: limit,
    });
    if (error || !data) return [];
    const raw = data as { items?: Array<Record<string, any>> };
    return (raw.items ?? []).map((r) => ({
      id: String(r.id),
      source: String(r.source ?? 'cron'),
      status: String(r.status ?? 'success'),
      releasedCount: Number(r.releasedCount ?? 0),
      minutes: Number(r.minutes ?? 45),
      message: r.message ?? null,
      startedAt: r.startedAt ?? '',
      finishedAt: r.finishedAt ?? '',
      createdAt: r.createdAt ?? '',
    }));
  } catch (err) {
    console.warn('[inventory] sweep history failed:', err);
    return [];
  }
}

export async function fetchReservationSweepStatus(): Promise<ReservationSweepStatus | null> {
  if (!isSupabaseConfigured || !supabase) return null;
  try {
    const { data, error } = await supabase.rpc('get_reservation_sweep_status');
    if (error || !data) return null;
    const raw = data as Record<string, any>;
    const last = raw.lastRun as Record<string, any> | null | undefined;
    return {
      active: Boolean(raw.active),
      intervalMinutes: Number(raw.intervalMinutes ?? 15),
      lastRun: last
        ? {
            status: last.status ?? null,
            source: last.source ?? null,
            releasedCount:
              last.releasedCount == null ? null : Number(last.releasedCount),
            startTime: last.startTime ?? null,
            endTime: last.endTime ?? null,
            message: last.message ?? null,
          }
        : null,
    };
  } catch (err) {
    console.warn('[inventory] sweep status failed:', err);
    return null;
  }
}